import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  Plus,
  Eye,
  Printer,
  Code,
  Edit,
  Trash2,
  Star,
  StarOff,
  Lock,
  Search,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { useDocumentTemplates } from '@/hooks/react-query/useTemplates';
import { DocumentTemplateList } from '@/types/template';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { formatDate } from '@/utils/formatters';
import { useToast } from '@/hooks/useToast';
import {
  TemplateSource,
  TemplateCategory,
  CATEGORY_LABELS,
  DOC_PAGE_SIZE,
  getCategoryBadgeColor,
} from '../constants';
import { DocumentTemplatePreviewDialog } from './DocumentTemplatePreviewDialog';
import { DocumentTemplateSourceDialog } from './DocumentTemplateSourceDialog';
import { DeleteDocumentTemplateDialog } from './DeleteDocumentTemplateDialog';

function getSourceBadge(source: TemplateSource) {
  if (source === 'SYSTEM') {
    return (
      <Badge
        variant="outline"
        className="border-slate-300 text-slate-600 gap-1"
      >
        <Lock className="h-3 w-3" />
        System
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-sky-300 text-sky-700 gap-1">
      <Edit className="h-3 w-3" />
      Custom
    </Badge>
  );
}

function TemplatesPlaceholder({ isLoading }: { isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  return (
    <Alert>
      <AlertDescription>
        No templates match the current filters.
      </AlertDescription>
    </Alert>
  );
}

export function DocumentTemplateTable() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    documentTemplates,
    isLoadingDocuments,
    setDefaultDocumentTemplate,
    isSettingDefaultDocument,
    deactivateDocumentTemplate,
    isDeactivatingDocument,
    previewHtmlDocumentTemplate,
  } = useDocumentTemplates();

  // Filter & pagination state
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<
    TemplateCategory | 'all'
  >('all');
  const [sourceFilter, setSourceFilter] = useState<TemplateSource | 'all'>(
    'all'
  );
  const [page, setPage] = useState(1);

  // Dialog state
  const [showPreview, setShowPreview] = useState(false);
  const [previewingTemplate, setPreviewingTemplate] =
    useState<DocumentTemplateList | null>(null);
  const [showSource, setShowSource] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const filteredTemplates = useMemo(() => {
    let result = documentTemplates || [];
    if (categoryFilter !== 'all') {
      result = result.filter((t) => t.template_type === categoryFilter);
    }
    if (sourceFilter !== 'all') {
      result = result.filter((t) => t.source === sourceFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          (t.description || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [documentTemplates, categoryFilter, sourceFilter, searchQuery]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredTemplates.length / DOC_PAGE_SIZE)
  );
  const safePage = Math.min(page, totalPages);
  const paginatedTemplates = filteredTemplates.slice(
    (safePage - 1) * DOC_PAGE_SIZE,
    safePage * DOC_PAGE_SIZE
  );

  const handleCreate = () => {
    navigate('/app/admin/templates/document/new');
  };

  const handleEdit = (template: DocumentTemplateList) => {
    navigate(`/app/admin/templates/document/${template.id}`);
  };

  const handlePreview = (template: DocumentTemplateList) => {
    setPreviewingTemplate(template);
    setShowPreview(true);
  };

  const handlePdfPreview = async (template: DocumentTemplateList) => {
    try {
      const result = await previewHtmlDocumentTemplate(template.id);
      const win = window.open('', '_blank');
      if (!win) {
        toast({
          title: 'Error',
          description: 'Pop-up blocked. Please allow pop-ups for this site.',
          variant: 'destructive',
        });
        return;
      }
      win.document.write(result.html);
      win.document.close();
      win.onload = () => win.print();
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to generate PDF preview',
        variant: 'destructive',
      });
    }
  };

  const handleViewSource = (template: DocumentTemplateList) => {
    setPreviewingTemplate(template);
    setShowSource(true);
  };

  const handleToggleActive = async (template: DocumentTemplateList) => {
    try {
      if (template.is_active) {
        await deactivateDocumentTemplate(template.id);
        toast({
          title: 'Template deactivated',
          description: `"${template.name}" has been deactivated`,
        });
      } else {
        await setDefaultDocumentTemplate(template.id);
        toast({
          title: 'Template activated',
          description: `"${template.name}" is now the active template for ${CATEGORY_LABELS[template.template_type as TemplateCategory] || template.template_type}`,
        });
      }
    } catch {
      toast({
        title: 'Error',
        description: `Failed to ${template.is_active ? 'deactivate' : 'activate'} template`,
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle></CardTitle>
              <CardDescription>
                HTML templates for generating PDF documents. System templates
                are read-only defaults shipped with the platform.
              </CardDescription>
            </div>
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="pl-9"
              />
            </div>
            <Select
              value={categoryFilter}
              onValueChange={(v) => {
                setCategoryFilter(v as TemplateCategory | 'all');
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="distribution">Distribution</SelectItem>
                <SelectItem value="drawdown">Drawdown</SelectItem>
                <SelectItem value="report">Report</SelectItem>
                <SelectItem value="notice">Notice</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={sourceFilter}
              onValueChange={(v) => {
                setSourceFilter(v as TemplateSource | 'all');
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="SYSTEM">System</SelectItem>
                <SelectItem value="CUSTOM">Custom</SelectItem>
              </SelectContent>
            </Select>
            {(searchQuery ||
              categoryFilter !== 'all' ||
              sourceFilter !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setCategoryFilter('all');
                  setSourceFilter('all');
                  setPage(1);
                }}
              >
                Clear filters
              </Button>
            )}
          </div>

          {/* Table */}
          {isLoadingDocuments || filteredTemplates.length === 0 ? (
            <TemplatesPlaceholder isLoading={isLoadingDocuments} />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Last Updated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedTemplates.map((template) => (
                    <TableRow
                      key={template.id}
                      className={
                        template.source === 'SYSTEM' ? 'bg-muted/20' : undefined
                      }
                    >
                      <TableCell className="font-medium">
                        <div>
                          <div className="flex items-center gap-2">
                            {template.name}
                            {template.source === 'SYSTEM' && (
                              <Lock className="h-3 w-3 text-muted-foreground" />
                            )}
                            {template.is_active && (
                              <Badge
                                variant="default"
                                className="bg-amber-500 hover:bg-amber-600 gap-1"
                              >
                                Active
                              </Badge>
                            )}
                          </div>
                          {template.description && (
                            <div className="text-sm text-muted-foreground mt-0.5 max-w-md">
                              {template.description}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={getCategoryBadgeColor(
                            template.template_type
                          )}
                        >
                          {CATEGORY_LABELS[
                            template.template_type as TemplateCategory
                          ] || template.template_type}
                        </Badge>
                      </TableCell>
                      <TableCell>{getSourceBadge(template.source)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        v{template.version}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(template.updated_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePreview(template)}
                            title="Preview"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePdfPreview(template)}
                            title="PDF Preview"
                          >
                            <Printer className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewSource(template)}
                            title="View Source"
                          >
                            <Code className="h-4 w-4" />
                          </Button>
                          {template.source === 'CUSTOM' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(template)}
                              title="Edit Template"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(template)}
                            className={
                              template.is_active
                                ? 'text-amber-500 hover:text-amber-700 hover:bg-amber-50'
                                : 'text-muted-foreground hover:text-amber-500 hover:bg-amber-50'
                            }
                            title={
                              template.is_active
                                ? 'Deactivate'
                                : 'Set as Active'
                            }
                            disabled={
                              isSettingDefaultDocument || isDeactivatingDocument
                            }
                          >
                            {template.is_active ? (
                              <Star className="h-4 w-4 fill-current" />
                            ) : (
                              <StarOff className="h-4 w-4" />
                            )}
                          </Button>
                          {template.source === 'CUSTOM' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteId(template.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between pt-2">
                <p className="text-sm text-muted-foreground">
                  {filteredTemplates.length} template
                  {filteredTemplates.length !== 1 ? 's' : ''}
                  {documentTemplates &&
                    filteredTemplates.length !== documentTemplates.length &&
                    ` (filtered from ${documentTemplates.length})`}
                </p>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={safePage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground tabular-nums">
                      Page {safePage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={safePage >= totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <DocumentTemplatePreviewDialog
        open={showPreview}
        onOpenChange={setShowPreview}
        template={previewingTemplate}
      />
      <DocumentTemplateSourceDialog
        open={showSource}
        onOpenChange={setShowSource}
        template={previewingTemplate}
      />
      <DeleteDocumentTemplateDialog
        open={!!deleteId}
        onOpenChange={(open) => {
          if (!open) setDeleteId(null);
        }}
        templateId={deleteId}
      />
    </>
  );
}
